import { useEffect, useState, type ReactNode } from 'react';
import {
  CalendarCheck,
  CircleDollarSign,
  Gavel,
  GraduationCap,
  LayoutDashboard,
  ListChecks,
  LogIn,
  LogOut,
  Mail,
  Megaphone,
  MessageCircle,
  MessageSquare,
  ReceiptText,
  Settings2,
  Share2,
  ShieldCheck,
  ShoppingBag,
  Ticket,
  UserCircle,
  Users
} from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import { getAppShareInfo, isNative, shareApp } from '../native';
import { formatMoney, roleLabel } from '../utils';
import { AccessBanner } from './AccessBanner';
import { NetworkStatusBanner } from './NetworkStatusBanner';
import { NotificationBell } from './NotificationBell';

export type ViewKey = 'dashboard' | 'checkin' | 'feedback' | 'tutorials' | 'center' | 'commissions' | 'market' | 'auctions' | 'notices' | 'board' | 'chat' | 'members' | 'settings' | 'redeem' | 'audit' | 'profile';

const desktopNav: Array<{ key: ViewKey; label: string; icon: typeof LayoutDashboard; group?: 'staff' }> = [
  { key: 'dashboard', label: '总览', icon: LayoutDashboard },
  { key: 'checkin', label: '签到', icon: CalendarCheck },
  { key: 'commissions', label: '委托大厅', icon: ListChecks },
  { key: 'center', label: '交易中心', icon: ShoppingBag },
  { key: 'auctions', label: '法拍场', icon: Gavel },
  { key: 'notices', label: '公告', icon: Megaphone },
  { key: 'board', label: '留言板', icon: MessageSquare },
  { key: 'chat', label: '站内聊天', icon: MessageCircle },
  { key: 'feedback', label: '意见反馈', icon: Mail },
  { key: 'tutorials', label: '使用教程', icon: GraduationCap },
  { key: 'members', label: '成员管理', icon: Users, group: 'staff' },
  { key: 'redeem', label: '兑换码', icon: Ticket, group: 'staff' },
  { key: 'settings', label: '系统设置', icon: Settings2, group: 'staff' },
  { key: 'audit', label: '审计日志', icon: ReceiptText, group: 'staff' },
  { key: 'profile', label: '我的账户', icon: UserCircle }
];

const mobileNav: Array<{ key: ViewKey; label: string; icon: typeof LayoutDashboard }> = [
  { key: 'dashboard', label: '总览', icon: LayoutDashboard },
  { key: 'checkin', label: '签到', icon: CalendarCheck },
  { key: 'commissions', label: '委托', icon: ListChecks },
  { key: 'center', label: '市场', icon: ShoppingBag },
  { key: 'auctions', label: '拍卖', icon: Gavel },
  { key: 'board', label: '留言', icon: MessageSquare },
  { key: 'chat', label: '聊天', icon: MessageCircle },
  { key: 'profile', label: '我的', icon: UserCircle }
];

export function AppShell({ view, onNavigate, onOpenAuth, children }: { view: ViewKey; onNavigate: (view: ViewKey) => void; onOpenAuth: () => void; children: ReactNode }) {
  const { user, settings, permissions, logout, refresh } = useAuth();
  const { confirm, toast } = useUi();
  const [accountOpen, setAccountOpen] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const visibleDesktopNav = desktopNav.filter(item => !item.group || permissions.isStaff);
  const current = desktopNav.find(item => item.key === view);
  const appName = isNative ? getAppShareInfo(settings.siteName).name : settings.siteName;
  const shareInfo = getAppShareInfo(appName);

  useEffect(() => setAccountOpen(false), [view]);

  useEffect(() => {
    const canChat = Boolean(user && (user.boundToGame || permissions.isSuperadmin));
    if (!canChat) {
      setChatUnreadCount(0);
      return;
    }
    let cancelled = false;
    const loadUnread = async () => {
      try {
        const data = await api<{ total: number }>('/api/chat/unread');
        if (!cancelled) setChatUnreadCount(Math.max(0, Number(data.total || 0)));
      } catch {
        // Keep the last known count when the network is temporarily unavailable.
      }
    };
    void loadUnread();
    const timer = window.setInterval(() => void loadUnread(), 20_000);
    const onUnreadChanged = () => void loadUnread();
    window.addEventListener('lspd:chat-unread-updated', onUnreadChanged);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('lspd:chat-unread-updated', onUnreadChanged);
    };
  }, [user?.id, user?.boundToGame, permissions.isSuperadmin]);

  const doLogout = async () => {
    const approved = await confirm({ title: '退出登录', body: '确认退出当前账号？', danger: true, confirmText: '退出登录' });
    if (approved) await logout();
  };

  const doShare = async () => {
    setAccountOpen(false);
    const result = await shareApp(shareInfo.name, shareInfo.url);
    if (result === 'copied') toast('App 链接已复制', 'success');
    if (result === 'failed') toast('分享失败，请稍后重试', 'error');
  };

  return (
    <div className="app-shell prism-console">
      <aside className="sidebar prism-sidebar">
        <div className="brand-block prism-brand">
          <button className="brand-home" onClick={() => onNavigate('dashboard')}>
            <span className="brand-mark prism-brand-mark"><ShieldCheck size={21} /></span>
            <span><strong>{appName}</strong><small>{isNative ? 'SERVER ASSISTANT' : 'SERVER DISPATCH CENTER'}</small></span>
          </button>
        </div>
        <nav className="side-nav prism-nav">
          <span className="prism-nav-label">工作台</span>
          {visibleDesktopNav.filter(item => item.group !== 'staff').map(item => {
            const Icon = item.icon;
            return <button key={item.key} className={'side-nav-item prism-nav-item ' + (view === item.key ? 'active' : '')} onClick={() => onNavigate(item.key)}><Icon size={18} /><span>{item.label}</span>{item.key === 'chat' && chatUnreadCount > 0 && <span className="nav-unread-badge">{chatUnreadCount > 99 ? '99+' : chatUnreadCount}</span>}</button>;
          })}
          {permissions.isStaff && <span className="prism-nav-label prism-nav-gap">管理</span>}
          {visibleDesktopNav.filter(item => item.group === 'staff').map(item => {
            const Icon = item.icon;
            return <button key={item.key} className={'side-nav-item prism-nav-item ' + (view === item.key ? 'active' : '')} onClick={() => onNavigate(item.key)}><Icon size={18} /><span>{item.label}</span>{item.key === 'chat' && chatUnreadCount > 0 && <span className="nav-unread-badge">{chatUnreadCount > 99 ? '99+' : chatUnreadCount}</span>}</button>;
          })}
        </nav>
        <div className="sidebar-account prism-sidebar-user">
          {user ? (
            <>
              <span className="account-avatar prism-avatar"><AvatarFace avatar={user.avatar} name={user.username} /></span>
              <div className="account-copy"><strong>{user.displayName}</strong><small>{roleLabel[user.role]} · {formatMoney(user.balanceCents, settings.currency)}</small></div>
            </>
          ) : <button className="button primary wide" onClick={onOpenAuth}><LogIn size={17} /> 登录 / 注册</button>}
        </div>
      </aside>

      <div className="workspace prism-workspace">
        <AccessBanner
          user={user}
          isStaff={permissions.isStaff}
          onLogin={onOpenAuth}
          onGoProfile={() => onNavigate('profile')}
          onRefresh={refresh}
        />
        <NetworkStatusBanner />
        <header className="mobile-header prism-topbar">
          <button className="mobile-brand" onClick={() => onNavigate('dashboard')}>
            <span className="brand-mark prism-brand-mark"><ShieldCheck size={19} /></span>
            <strong>{appName}</strong>
          </button>
          <div className="prism-topbar-title"><span>{appName}</span><strong>{current?.label || '工作台'}</strong></div>
          <div className="prism-topbar-actions">
            <span className="prism-status"><i />在线</span>
            <button type="button" className="mobile-help-button" onClick={() => onNavigate('tutorials')} aria-label="使用教程" title="使用教程"><GraduationCap size={18} /></button>
            {user && <NotificationBell onNavigate={onNavigate} />}
            {user ? (
              <div className="account-menu-wrap">
                <button className="topbar-account" onClick={() => setAccountOpen(value => !value)}>
                  <span className="account-avatar prism-avatar"><AvatarFace avatar={user.avatar} name={user.username} /></span>
                  <span className="mobile-balance"><CircleDollarSign size={14} />{formatMoney(user.balanceCents, settings.currency)}</span>
                  <span className="desktop-account-copy"><strong>{user.displayName}</strong><small>{roleLabel[user.role]}</small></span>
                </button>
                {accountOpen && (
                  <div className="account-popover">
                    <div><strong>{user.displayName}</strong><small>@{user.username} · {roleLabel[user.role]}</small></div>
                    <button onClick={() => onNavigate('profile')}><UserCircle size={16} />我的账户</button>
                    <button onClick={() => onNavigate('feedback')}><Mail size={16} />意见反馈</button>
                    <button onClick={() => onNavigate('tutorials')}><GraduationCap size={16} />使用教程</button>
                    <button onClick={() => void doShare()}><Share2 size={16} />分享{shareInfo.name}</button>
                    <button className="danger-text" onClick={() => void doLogout()}><LogOut size={16} />退出登录</button>
                  </div>
                )}
              </div>
            ) : <button className="button primary" onClick={onOpenAuth}><LogIn size={16} /> 登录</button>}
          </div>
        </header>
        <main className="content prism-content"><div className="page-enter" key={view}>{children}</div></main>
      </div>

      <nav className="mobile-tabbar">
        {mobileNav.map(item => {
          const Icon = item.icon;
          const active = view === item.key || (item.key === 'profile' && ['members', 'redeem', 'settings', 'audit'].includes(view));
          return <button key={item.key} className={active ? 'active' : ''} onClick={() => onNavigate(item.key)}><Icon size={20} /><span>{item.label}</span>{item.key === 'chat' && chatUnreadCount > 0 && <span className="nav-unread-badge">{chatUnreadCount > 99 ? '99+' : chatUnreadCount}</span>}</button>;
        })}
      </nav>
    </div>
  );
}


function AvatarFace({ avatar, name }: { avatar?: string | null; name: string }) {
  return avatar ? <img className="avatar-image" src={avatar} alt="" /> : <>{name.slice(0, 1).toUpperCase()}</>;
}



